import { grey } from '@mui/material/colors';
import { FundValueBridge } from 'further-types/lp/period-end-report';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { round } from 'lodash';
import { FC } from 'react';
import { CalendarDay, numberToShorthandCurrencyString } from '../../../utils';
import {
  buildFundValueBridgeChartData,
  prepareFundValueBridgeChartRenderData,
} from '../chart-data';
import { MIN_BRIDGE_BAR_HEIGHT_PX } from './constants';
import createLinearScale from './create-linear-scale';
import formatYAxisTick from './format-y-axis-tick';
import { buildFundValueBridgeChartLayout } from './get-chart-layout';
import { getColumnLayout, getVerticalGridCoordinates } from './layout';
import { getPdfValueTickDy, getPdfYAxisTickDy } from './pdf-chart-fonts';
import wrapSvgLabel from './wrap-svg-label';

const CHART_COLORS = {
  grid: grey[300],
  referenceLine: grey[600],
  valueText: grey[900],
  labelText: grey[500],
  background: '#FFFFFF',
} as const;

type Props = {
  baseCurrency: CurrencyCode;
  periodStart: CalendarDay;
  periodEnd: CalendarDay;
  fundValueBridge: FundValueBridge;
  yAxisOrientation?: 'left' | 'right';
  variant?: 'portal' | 'pdf';
  availableChartWidth?: number;
  highlightColor?: string;
};

const FundValueBridgeChartSvg: FC<Props> = ({
  baseCurrency,
  periodStart,
  periodEnd,
  fundValueBridge,
  yAxisOrientation = 'right',
  variant = 'portal',
  availableChartWidth,
  highlightColor,
}) => {
  const builtChartData = buildFundValueBridgeChartData({
    bridge: fundValueBridge,
    periodStart,
    periodEnd,
  });

  if (!builtChartData) {
    return null;
  }

  const { chartRows, yAxis } =
    prepareFundValueBridgeChartRenderData(builtChartData);
  const {
    domain,
    ticks,
    horizontalGridValues,
    hiddenTickValues,
    referenceLineValue,
    showReferenceLine,
    valueBandTop,
    valueBandFloor,
    yAxisLabelOffset,
  } = yAxis;

  const formatAbsoluteYAxisTick = (zoomedTick: number) =>
    formatYAxisTick(round(zoomedTick + yAxisLabelOffset, 2), baseCurrency);

  const yAxisTickLabels = ticks
    .filter((tick) => !hiddenTickValues.includes(tick))
    .map((tick) => formatAbsoluteYAxisTick(tick));

  const layout = buildFundValueBridgeChartLayout({
    variant,
    columnCount: chartRows.length,
    yAxisTickLabels,
    availableChartWidth,
    highlightColor,
  });

  const {
    barWidth,
    chartContentWidth,
    chartWidth,
    yAxisWidth,
    chartHeight,
    chartTopMargin,
    plotBottom,
    axisLabelFontSize,
    valueFontSize,
    labelFontSize,
    labelHorizontalPadding,
    labelLineHeight,
    labelMaxLines,
    valueToLabelGap,
    yAxisGridGap,
    bridgeBarColor,
    gridLineThickness,
    referenceLineStrokeWidth,
    chartPaddingLeft,
  } = layout;

  const isPdfVariant = variant === 'pdf';
  const chartFontFamily = 'MaisonNeueNew';
  const pdfYAxisTickDy = getPdfYAxisTickDy(axisLabelFontSize);
  const gridColor = CHART_COLORS.grid;

  const plotTop = chartTopMargin;
  const yAxisOnRight = yAxisOrientation === 'right';
  const plotLeft = yAxisOnRight ? chartPaddingLeft : yAxisWidth;
  const yAxisTickX = yAxisOnRight
    ? plotLeft + chartContentWidth + yAxisGridGap
    : plotLeft - yAxisGridGap;
  const yAxisTickTextAnchor = yAxisOnRight ? 'start' : 'end';
  const yScale = createLinearScale(domain, plotTop, plotBottom);

  const formattedColumnDisplayValues = chartRows.map((row) =>
    numberToShorthandCurrencyString(row.displayValue, {
      currency: baseCurrency,
      negativeInBrackets: true,
    }),
  );

  const valueBandTopY = Math.round(yScale(valueBandTop));
  const valueBandBottomY = Math.round(yScale(valueBandFloor));
  const valueRowPixelHeight = valueBandBottomY - valueBandTopY;
  const pdfValueTickDy = isPdfVariant
    ? getPdfValueTickDy(valueFontSize, valueRowPixelHeight)
    : 0;
  const columnSlotWidth = chartContentWidth / chartRows.length;
  const labelBaseY = valueBandBottomY + valueToLabelGap;
  const labelLineHeightPx = labelFontSize * labelLineHeight;
  const verticalGridBottomY = Math.round(yScale(valueBandFloor));
  const verticalGridXCoordinates = getVerticalGridCoordinates(
    plotLeft,
    chartContentWidth,
    chartRows.length,
  ).map((x) => (isPdfVariant ? Math.round(x) : x));

  const toAxisTickY = (value: number) => Math.round(yScale(value));
  const referenceLineY = showReferenceLine
    ? toAxisTickY(referenceLineValue) + (isPdfVariant ? 0 : 0.5)
    : undefined;
  const plotRightX = plotLeft + chartContentWidth;
  const horizontalGridLineYs = horizontalGridValues.map((gridValue) =>
    toAxisTickY(gridValue),
  );
  const pdfTopHorizontalGridY =
    horizontalGridLineYs.length > 0
      ? Math.min(...horizontalGridLineYs)
      : plotTop;
  const pdfVerticalGridTopY = pdfTopHorizontalGridY;
  const pdfVerticalGridBottomY = verticalGridBottomY + gridLineThickness;
  const pdfVerticalGridHeight = pdfVerticalGridBottomY - pdfVerticalGridTopY;
  const pdfHorizontalGridWidth = plotRightX - plotLeft + gridLineThickness;

  const pdfHorizontalGridLines = horizontalGridValues.map((gridValue) => {
    const y = toAxisTickY(gridValue);

    return (
      <rect
        key={`h-${gridValue}`}
        x={plotLeft}
        y={y}
        width={pdfHorizontalGridWidth}
        height={gridLineThickness}
      />
    );
  });

  const pdfVerticalGridLines = verticalGridXCoordinates.map((x) => (
    <rect
      key={`v-${x}`}
      x={x}
      y={pdfVerticalGridTopY}
      width={gridLineThickness}
      height={pdfVerticalGridHeight}
    />
  ));

  const portalHorizontalGridLines = horizontalGridValues.map((gridValue) => {
    const y = toAxisTickY(gridValue);

    return (
      <line
        key={`h-${gridValue}`}
        x1={plotLeft}
        x2={plotRightX}
        y1={y}
        y2={y}
        stroke={gridColor}
        strokeWidth={gridLineThickness}
        strokeLinecap="butt"
      />
    );
  });

  const portalVerticalGridLines = verticalGridXCoordinates.map((x) => (
    <line
      key={`v-${x}`}
      x1={x}
      x2={x}
      y1={plotTop}
      y2={verticalGridBottomY}
      stroke={gridColor}
      strokeWidth={gridLineThickness}
      strokeLinecap="butt"
    />
  ));

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={chartWidth}
      height={chartHeight}
      viewBox={`0 0 ${chartWidth} ${chartHeight}`}
      style={{ backgroundColor: CHART_COLORS.background }}
    >
      {isPdfVariant ? (
        <g fill={gridColor} shapeRendering="crispEdges">
          {pdfVerticalGridLines}
          {pdfHorizontalGridLines}
        </g>
      ) : (
        <>
          {portalHorizontalGridLines}
          {portalVerticalGridLines}
        </>
      )}
      {referenceLineY !== undefined ? (
        <line
          x1={plotLeft}
          x2={plotRightX}
          y1={referenceLineY}
          y2={referenceLineY}
          stroke={CHART_COLORS.referenceLine}
          strokeWidth={referenceLineStrokeWidth}
          strokeLinecap="butt"
        />
      ) : null}
      {chartRows.map((row, index) => {
        if (row.value <= 0) {
          return null;
        }

        const { x, width } = getColumnLayout(
          plotLeft,
          chartContentWidth,
          chartRows.length,
          index,
        );
        const barX = Math.round(x + (width - barWidth) / 2);
        const yTop = Math.round(yScale(row.base + row.value));
        const yBottom = Math.round(yScale(row.base));
        const renderedHeight = Math.max(
          yBottom - yTop,
          MIN_BRIDGE_BAR_HEIGHT_PX,
        );

        return (
          <rect
            key={`bar-${row.key}`}
            x={barX}
            y={yTop}
            width={barWidth}
            height={renderedHeight}
            fill={bridgeBarColor}
          />
        );
      })}
      {ticks.map((tick) => {
        if (hiddenTickValues.includes(tick)) {
          return null;
        }

        const label = formatAbsoluteYAxisTick(tick);

        if (!label) {
          return null;
        }

        const y = toAxisTickY(tick);

        return (
          <text
            key={`y-tick-${tick}`}
            x={yAxisTickX}
            y={y}
            dy={isPdfVariant ? pdfYAxisTickDy : 4}
            textAnchor={yAxisTickTextAnchor}
            fill={CHART_COLORS.valueText}
            fontSize={axisLabelFontSize}
            fontWeight={400}
            fontFamily={chartFontFamily}
          >
            {label}
          </text>
        );
      })}
      {chartRows.map((row, index) => {
        const formattedDisplayValue = formattedColumnDisplayValues[index];

        if (formattedDisplayValue === undefined) {
          return null;
        }

        const centerX = plotLeft + (index + 0.5) * columnSlotWidth;
        const labelWrapWidth = columnSlotWidth - labelHorizontalPadding * 2;
        const labelLines = wrapSvgLabel(
          row.label,
          labelWrapWidth,
          labelMaxLines,
          labelFontSize,
        );

        return (
          <g key={`column-${row.key}`}>
            <text
              x={centerX}
              y={valueBandTopY + valueRowPixelHeight / 2}
              dy={isPdfVariant ? pdfValueTickDy : 0}
              textAnchor="middle"
              dominantBaseline={isPdfVariant ? 'auto' : 'middle'}
              fill={CHART_COLORS.valueText}
              fontSize={valueFontSize}
              fontWeight={500}
              fontFamily={chartFontFamily}
            >
              {formattedDisplayValue}
            </text>
            {labelLines.map((line, lineIndex) => (
              <text
                key={`${row.key}-label-${line}`}
                x={centerX}
                y={labelBaseY + lineIndex * labelLineHeightPx}
                textAnchor="middle"
                dominantBaseline="hanging"
                fill={CHART_COLORS.labelText}
                fontSize={labelFontSize}
                fontWeight={isPdfVariant ? 400 : 500}
                fontFamily={chartFontFamily}
              >
                {line}
              </text>
            ))}
          </g>
        );
      })}
    </svg>
  );
};

export default FundValueBridgeChartSvg;
